import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { ConnectionState } from '../types/connection.types';
import { useProfile } from './useProfile';


export const connectionEndpoints = {
  getState: (targetUserId: string) => `/connections/state/${targetUserId}`,
  request: '/connections/request',
  approve: (connectionId: string) => `/connections/${connectionId}/approve`,
  reject: (connectionId: string) => `/connections/${connectionId}/reject`,
} as const;
/**
 * Hook to manage connection state between users
 */
export const useConnection = (userId: string, initialState?: ConnectionState) => {
  const { profile } = useProfile();
  const queryClient = useQueryClient();
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    initialState || {
      isConnected: false,
      isPending: false,
      isReceiver: false,
    }
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialFetchDone = useRef(false);

  const getState = async () => {
    try {
      setLoading(true);
      const response = await api.get<ConnectionState>(connectionEndpoints.getState(userId));
      setConnectionState(response.data);
      return response.data;
    } catch (err) {
      setError('Error fetching connection state');
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialState) {
      setConnectionState(initialState);
    } else if (userId && profile?.id && !initialFetchDone.current) {
      initialFetchDone.current = true;
      getState();
    }
  }, [userId, profile?.id, initialState]);

  const sendRequest = async () => {
    if (!profile?.id) return;

    try {
      setLoading(true);
      const response = await api.post(connectionEndpoints.request, {
        receiverId: userId,
      });

      await refreshQueries();

      setConnectionState({
        ...connectionState,
        isPending: true,
        isReceiver: false,
        connectionId: response.data.connection.id,
      });
    } catch (err) {
      setError('Error sending connection request');
    } finally {
      setLoading(false);
    }
  };

  const refreshQueries = async () => {
    await queryClient.invalidateQueries({ queryKey: ["connections"] });
    await queryClient.invalidateQueries({ queryKey: ["members"] });
  };

  const approveRequest = async () => {
    if (!connectionState.connectionId) return;

    try {
      setLoading(true);
      await api.patch(connectionEndpoints.approve(connectionState.connectionId));
      await refreshQueries();
      await getState();
    } catch (err) {
      setError('Error approving connection request');
    } finally {
      setLoading(false);
    }
  };

  const rejectRequest = async () => {
    if (!connectionState.connectionId) return;

    try {
      setLoading(true);
      await api.patch(connectionEndpoints.reject(connectionState.connectionId));
      await refreshQueries();
      await getState();
    } catch (err) {
      setError('Error rejecting connection request');
    } finally {
      setLoading(false);
    }
  };

  // If no profile, return default state
  if (!profile?.id) {
    return {
      ...connectionState,
      loading: false,
      error: null,
      sendRequest: () => { },
      approveRequest: () => { },
      rejectRequest: () => { },
    };
  }

  return {
    ...connectionState,
    loading,
    error,
    sendRequest,
    approveRequest,
    rejectRequest,
  };
};
